/**
 * PreImportRecommendationsPanel
 * Displays intelligent, opinionated recommendations before bank import
 * Part of the intelligent import system
 */

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  AlertCircle,
  CheckCircle2,
  Info,
  Lightbulb,
  Brain,
  TrendingUp,
  FileText,
  Settings2,
  ChevronRight,
} from "lucide-react";

// Types matching the backend
interface ImportRecommendation {
  id: number;
  type: "format" | "vendor" | "pattern" | "attention" | "learning";
  priority: "high" | "medium" | "low";
  title: string;
  description: string;
  actionable: boolean;
  measurementKey?: string;
  metadata?: Record<string, any>;
}

interface PreImportAnalysisResult {
  fingerprint: {
    bankType: string;
    statementType: string;
    delimiter: string;
    encoding: string;
    detectedRows: number;
    confidence: number;
  };
  learnedPattern?: {
    id: number;
    timesEncountered: number;
    successRate: number;
    lastUsed: number;
    dateFormat?: string;
    vendorExtractionPattern?: string;
  };
  recommendations: ImportRecommendation[];
  previewStats: {
    estimatedTransactions: number;
    estimatedVendors: number;
    dateRange?: { from: string; to: string };
    hasBankPrefixes: boolean;
    prefixExamples: string[];
  };
  sessionId?: number;
}

interface PreImportRecommendationsPanelProps {
  analysis: PreImportAnalysisResult | null;
  isLoading?: boolean;
  onAcceptRecommendation?: (id: number) => void;
  onOverrideRecommendation?: (id: number) => void;
  onOpenTuning?: () => void;
  onProceed?: () => void;
}

function getRecommendationIcon(type: ImportRecommendation["type"]) {
  switch (type) {
    case "format":
      return <FileText className="h-4 w-4" />;
    case "vendor":
      return <Settings2 className="h-4 w-4" />;
    case "pattern":
      return <TrendingUp className="h-4 w-4" />;
    case "attention":
      return <AlertCircle className="h-4 w-4" />;
    case "learning":
      return <Brain className="h-4 w-4" />;
    default:
      return <Info className="h-4 w-4" />;
  }
}

function getPriorityColor(priority: ImportRecommendation["priority"]) {
  switch (priority) {
    case "high":
      return "destructive";
    case "medium":
      return "default";
    case "low":
      return "secondary";
  }
}

function getBankDisplayName(bankType: string): string {
  const names: Record<string, string> = {
    postfinance: "PostFinance",
    ubs: "UBS",
    credit_suisse: "Credit Suisse",
    raiffeisen: "Raiffeisen",
    zkb: "ZKB",
    unknown: "Unknown Bank",
  };
  return names[bankType] || bankType;
}

function getStatementTypeDisplay(type: string): string {
  const types: Record<string, string> = {
    bank_account: "Bank Account Statement",
    credit_card: "Credit Card Statement",
    unknown: "Statement",
  };
  return types[type] || type;
}

export default function PreImportRecommendationsPanel({
  analysis,
  isLoading,
  onAcceptRecommendation,
  onOverrideRecommendation,
  onOpenTuning,
  onProceed,
}: PreImportRecommendationsPanelProps) {
  if (isLoading) {
    return (
      <Card className="animate-pulse">
        <CardHeader>
          <div className="h-6 w-48 bg-muted rounded" />
          <div className="h-4 w-64 bg-muted rounded mt-2" />
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="h-16 bg-muted rounded" />
            <div className="h-16 bg-muted rounded" />
            <div className="h-16 bg-muted rounded" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!analysis) {
    return null;
  }

  const { fingerprint, learnedPattern, recommendations, previewStats } =
    analysis;
  const confidencePercent = Math.round(fingerprint.confidence * 100);
  const highPriorityCount = recommendations.filter(
    r => r.priority === "high"
  ).length;

  return (
    <div className="space-y-4">
      {/* Detection Summary */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Brain className="h-5 w-5 text-primary" />
                Pre-Import Analysis
              </CardTitle>
              <CardDescription>
                Smart recommendations for your import
              </CardDescription>
            </div>
            <Badge
              variant={
                confidencePercent >= 80
                  ? "default"
                  : confidencePercent >= 60
                    ? "secondary"
                    : "destructive"
              }
              className="text-sm"
            >
              {confidencePercent}% Confidence
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-3 bg-muted/50 rounded-lg">
              <div className="text-2xl font-bold">
                {previewStats.estimatedTransactions}
              </div>
              <div className="text-xs text-muted-foreground">Transactions</div>
            </div>
            <div className="text-center p-3 bg-muted/50 rounded-lg">
              <div className="text-2xl font-bold">
                {previewStats.estimatedVendors}
              </div>
              <div className="text-xs text-muted-foreground">Est. Vendors</div>
            </div>
            <div className="text-center p-3 bg-muted/50 rounded-lg">
              <div className="text-sm font-medium">
                {getBankDisplayName(fingerprint.bankType)}
              </div>
              <div className="text-xs text-muted-foreground">
                {getStatementTypeDisplay(fingerprint.statementType)}
              </div>
            </div>
            <div className="text-center p-3 bg-muted/50 rounded-lg">
              {learnedPattern ? (
                <>
                  <div className="text-sm font-medium text-[var(--success)]">
                    {learnedPattern.timesEncountered}x Imported
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {learnedPattern.successRate}% Success Rate
                  </div>
                </>
              ) : (
                <>
                  <div className="text-sm font-medium">First Import</div>
                  <div className="text-xs text-muted-foreground">
                    New format detected
                  </div>
                </>
              )}
            </div>
          </div>

          {previewStats.dateRange && (
            <div className="mt-3 text-sm text-muted-foreground text-center">
              Date range: {previewStats.dateRange.from} to{" "}
              {previewStats.dateRange.to}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recommendations */}
      {recommendations.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Lightbulb className="h-5 w-5 text-[var(--warning)]" />
                Recommendations
                {highPriorityCount > 0 && (
                  <Badge variant="destructive" className="ml-2">
                    {highPriorityCount} Important
                  </Badge>
                )}
              </CardTitle>
              {onOpenTuning && (
                <Button variant="outline" size="sm" onClick={onOpenTuning}>
                  <Settings2 className="h-4 w-4 mr-1" />
                  Tuning Options
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {recommendations.map(rec => (
              <Alert
                key={rec.id}
                variant={rec.priority === "high" ? "destructive" : "default"}
                className="flex items-start gap-3"
              >
                <div className="flex-shrink-0 mt-0.5">
                  {getRecommendationIcon(rec.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <AlertTitle className="text-sm font-medium flex items-center gap-2">
                    {rec.title}
                    <Badge
                      variant={getPriorityColor(rec.priority)}
                      className="text-xs"
                    >
                      {rec.priority}
                    </Badge>
                  </AlertTitle>
                  <AlertDescription className="text-sm mt-1">
                    {rec.description}
                  </AlertDescription>
                  {rec.metadata?.prefixExamples && (
                    <div className="mt-2 text-xs text-muted-foreground bg-muted/50 p-2 rounded font-mono">
                      {rec.metadata.prefixExamples
                        .slice(0, 2)
                        .map((ex: string, i: number) => (
                          <div key={i} className="truncate">
                            {ex}
                          </div>
                        ))}
                    </div>
                  )}
                </div>
                {rec.actionable && onAcceptRecommendation && (
                  <div className="flex-shrink-0 flex gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onAcceptRecommendation(rec.id)}
                    >
                      <CheckCircle2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </Alert>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Bank Prefix Warning */}
      {previewStats.hasBankPrefixes &&
        previewStats.prefixExamples.length > 0 && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Bank Prefixes Detected</AlertTitle>
            <AlertDescription>
              <p className="mb-2">
                Your bank adds prefixes to descriptions. Smart Analysis will
                clean these for better vendor extraction:
              </p>
              <div className="text-xs font-mono bg-muted/50 p-2 rounded space-y-1">
                {previewStats.prefixExamples.slice(0, 3).map((ex, i) => (
                  <div key={i} className="truncate">
                    {ex}
                  </div>
                ))}
              </div>
            </AlertDescription>
          </Alert>
        )}

      {/* Action Buttons */}
      <div className="flex justify-end gap-2">
        {onOpenTuning && (
          <Button variant="outline" onClick={onOpenTuning}>
            <Settings2 className="h-4 w-4 mr-2" />
            Configure Tuning
          </Button>
        )}
        {onProceed && (
          <Button onClick={onProceed}>
            Continue with Import
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        )}
      </div>
    </div>
  );
}
